import React, { useMemo } from 'react';
import { Paper, Typography } from '@mui/material';
import { calculateLinearFit } from './pendulumMath';

interface GraphPlotProps {
  title: string;
  points: Array<{ x: number; y: number }>;
  xLabel: string;
  yLabel: string;
  fitThroughOrigin?: boolean;
  showUncertainty?: boolean;
}

const GraphPlot: React.FC<GraphPlotProps> = ({ title, points, xLabel, yLabel, fitThroughOrigin = false, showUncertainty = false }) => {
  const fit = useMemo(() => {
    if (points.length < 2) {
      return { slope: 0, intercept: 0, rSquared: 0 };
    }
    return fitThroughOrigin ? { slope: points.reduce((sum, { x, y }) => sum + x * y, 0) / points.reduce((sum, { x }) => sum + x ** 2, 0), intercept: 0, rSquared: 1 } : calculateLinearFit(points);
  }, [fitThroughOrigin, points]);

  const xMin = Math.min(...points.map(({ x }) => x));
  const xMax = Math.max(...points.map(({ x }) => x));
  const yMin = Math.min(...points.map(({ y }) => y));
  const yMax = Math.max(...points.map(({ y }) => y));

  const toSvgX = (x: number) => 40 + ((x - xMin) / (xMax - xMin || 1)) * 460;
  const toSvgY = (y: number) => 220 - ((y - yMin) / (yMax - yMin || 1)) * 160;

  const linePath = `M ${toSvgX(xMin)} ${toSvgY(fit.intercept + fit.slope * xMin)} L ${toSvgX(xMax)} ${toSvgY(fit.intercept + fit.slope * xMax)}`;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
      <svg width="100%" height="260" viewBox="0 0 520 260" role="img" aria-label={title}>
        <rect x="0" y="0" width="520" height="260" fill="#f8fafc" stroke="#dfe7f2" />
        <g stroke="#cdd6e3" strokeWidth="1">
          {[0, 1, 2, 3, 4].map((tick) => {
            const x = 40 + tick * 100;
            const y = 60 + tick * 40;
            return <React.Fragment key={`grid-${tick}`}>
              <line x1={x} y1={40} x2={x} y2={220} />
              <line x1={40} y1={y} x2={480} y2={y} />
            </React.Fragment>;
          })}
        </g>
        <line x1="40" y1="220" x2="480" y2="220" stroke="#334155" strokeWidth="2" />
        <line x1="40" y1="40" x2="40" y2="220" stroke="#334155" strokeWidth="2" />
        <path d={linePath} fill="none" stroke="#d97706" strokeWidth="3" />
        {points.map(({ x, y }, index) => {
          const px = toSvgX(x);
          const py = toSvgY(y);
          const uncertainty = showUncertainty ? 3 : 0;
          return (
            <g key={`${x}-${y}-${index}`}>
              {showUncertainty && <line x1={px} y1={py - uncertainty} x2={px} y2={py + uncertainty} stroke="#475569" strokeWidth="1.5" />}
              <circle cx={px} cy={py} r="4" fill="#2563eb" />
            </g>
          );
        })}
        <text x="250" y="246" textAnchor="middle" fontSize="12">{xLabel}</text>
        <text x="16" y="130" transform="rotate(-90 16 130)" textAnchor="middle" fontSize="12">{yLabel}</text>
      </svg>
      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
        slope = {fit.slope.toFixed(4)}, intercept = {fit.intercept.toFixed(4)}, R² = {fit.rSquared.toFixed(3)}
      </Typography>
    </Paper>
  );
};

export default GraphPlot;
